import RemoveIcon from 'imagesUwKluis/svg/close.svg';
import { bool, func, number, shape, array, string } from 'prop-types';
import Card from '../../shared/Card';
import Date from '../../shared/Date';
import Header from '../../shared/Header';
import Input from '../../shared/Input';
import { Box, Cluster, Stack } from '../../shared/layouts';
import Select from '../../shared/Select';
import RadioButton from '../../shared/RadioButton';
import DisplayLine from '../../shared/DisplayLine';
import { findTranslation } from '../../../utils/helpers';
import Errors from '../../shared/Errors';
import FormStack from '../../shared/layouts/FormStack';
import i18n from "../../../i18n";
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";
import {useTheme} from "@emotion/react";
import {useMediaQuery} from "@dnhb/node_package";

const getFrtDurationOptions = () => new Array(81).fill(null).map((_, year) => ({
  label: i18n.t('{{index}} jaar rentevast', {index: year}),
  value: Number(year * 12),
}));

const getNhgOptions = () => [
  { value: false, label: i18n.t('Nee') },
  { value: true, label: i18n.t('Ja') },
];

const getDurationYearsOptions = () => new Array(100).fill().map((item, i) =>
  ({ value: i, label: i18n.t('{{index}} jaar', {index: i}) })
);

const getDurationMonthsOptions = () => new Array(12).fill().map((item, i) => {
  if (i < 2) return { value: i, label: i18n.t('{{index}} maand', {index: i}) };
  return { value: i, label: i18n.t('{{index}} maanden', {index: i}) };
});

const LoanpartSection = ({
  loanparttype,
  loanpartnumber,
  originalAmount,
  currentAmount,
  startDate,
  duration,
  endDate,
  frtStartDate,
  frtDuration,
  frtEndDate,
  nhg,
  ltv,
  interest,
  interestExpense,
  repayment,
  premium,
  grossMonthlyPayment,
  index,
  mortgageIndex,
  removable,
  setLoanpart,
  removeLoanpart,
  errors,
  repaymentTypeOptions,
  canEditMortgage,
}) => {
  const durationInYears = Math.trunc(duration / 12);
  const durationInMonths = duration % 12;
  const durationErrors = errors[`${mortgageIndex}.loanparts.${index}.duration`];

  const readOnly = !canEditMortgage;
  const FormFiller = (props) => !readOnly && <Box {...props} />;

  const { t } = useTranslation();
  const { CurrencyIcon } = useIconLibrary();

  const frtDurationOptions = getFrtDurationOptions();
  const nhgOptions = getNhgOptions();
  const durationYearsOptions = getDurationYearsOptions();
  const durationMonthsOptions = getDurationMonthsOptions();

  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );

  return (
    <Card>
      <Stack space="m" width="100%">
        <Header
          title={
            <Cluster hGap="l">
              <span>{t('Leningdeel {{index}}', {index: index + 1})}</span>
              {removable && canEditMortgage && (
                <RemoveIcon
                  css={(theme) => ({
                    fill: theme?.colors?.alert || 'red',
                    cursor: 'pointer',
                    margin: '4px',
                    width: '1.5rem',
                    height: '1.5rem',
                  })}
                  onClick={removeLoanpart}
                />
              )}
            </Cluster>
          }
          icon={<CurrencyIcon height="2rem" width="2rem" />}
          variant="section"
        />
        <Stack divider={'default'} space={'l'}>
          <FormStack space="m" readOnly={readOnly}>
            {/* Row */}
            <Select
              autoFocus
              name="loanparttype"
              label={t("Aflosvorm")}
              onChange={setLoanpart}
              options={repaymentTypeOptions}
              value={loanparttype}
              errors={errors[`${mortgageIndex}.loanparts.${index}.loanparttype`]}
              readOnly={readOnly}
            />
            <Input
              name="loanpartnumber"
              label={t("Leningdeelnummer")}
              value={loanpartnumber}
              onChange={setLoanpart}
              errors={
                errors[`${mortgageIndex}.loanparts.${index}.loanpartnumber`]
              }
              placeholder={t("Vul je leningdeelnummer in")}
              readOnly={readOnly}
            />
            {/* Row */}
            <Box minWidth={'calc(100% - 24px)'}>
              <RadioButton
                name={`${mortgageIndex}_${index}_nhg`}
                label={
                  readOnly
                    ? (isMinDesktop ? t('Nationale Hypotheek Garantie (NHG)') : t('NHG'))
                    : t('Heb je Nationale Hypotheek Garantie (NHG) op dit leningdeel?')
                }
                direction={'row'}
                value={nhg}
                options={nhgOptions}
                onChange={(value) =>
                  setLoanpart({ target: { name: 'nhg', value } })
                }
                readOnly={readOnly}
              />
            </Box>
            <FormFiller display={'none'} />
            {/* Row */}
            <Input
              isCurrency
              label={t("Oorspronkelijke schuld")}
              name="originalAmount"
              onChange={setLoanpart}
              value={originalAmount}
              errors={
                errors[`${mortgageIndex}.loanparts.${index}.originalAmount`]
              }
              readOnly={readOnly}
            />
            <Input
              isCurrency
              name="currentAmount"
              label={t("Resterende schuld")}
              value={currentAmount}
              onChange={setLoanpart}
              errors={errors[`${mortgageIndex}.loanparts.${index}.currentAmount`]}
              readOnly={readOnly}
            />
            {/* Row */}
            <Date
              name="startDate"
              label={t("Ingangsdatum leningdeel")}
              onChange={setLoanpart}
              value={startDate !== null ? startDate : ''}
              errors={errors[`${mortgageIndex}.loanparts.${index}.startDate`]}
              readOnly={readOnly}
            />

            {readOnly ? (
              <DisplayLine
                label={t("Looptijd")}
                value={
                  duration &&
                  `
                    ${findTranslation(durationInYears, durationYearsOptions)}
                    ${
                      durationInMonths
                        ? ` en ${findTranslation(
                            durationInMonths,
                            durationMonthsOptions
                          )}`
                        : ''
                    }
                  `
                }
              />
            ) : (
              <Box>
                <Cluster flex={1} hGap="s">
                  <Select
                    name="durationInYears"
                    label={t("Looptijd")}
                    value={durationInYears}
                    onChange={(e) => {
                      const value = e.target.value;
                      const duration =
                        Number(value) * 12 + Number(durationInMonths);
                      setLoanpart({
                        target: { name: 'duration', value: duration },
                      });
                    }}
                    options={durationYearsOptions}
                    errors={durationErrors}
                    showTextErrors={false}
                    isSearchable
                  />

                  <Box mt="2xl">
                    <Select
                      name="durationInMonths"
                      value={durationInMonths}
                      onChange={(e) => {
                        const value = e.target.value;
                        const duration =
                          Number(durationInYears) * 12 + Number(value);
                        setLoanpart({
                          target: { name: 'duration', value: duration },
                        });
                      }}
                      options={durationMonthsOptions}
                      errors={durationErrors}
                      showTextErrors={false}
                    />
                  </Box>
                </Cluster>
                <Errors errors={durationErrors} />
              </Box>
            )}
            <Date
              name="endDate"
              label={t("Einddatum leningdeel")}
              onChange={setLoanpart}
              value={endDate !== null ? endDate : ''}
              errors={errors[`${mortgageIndex}.loanparts.${index}.endDate`]}
              readOnly={readOnly}
            />

            <FormFiller />

            {/* Row */}
            <Date
              name="frtStartDate"
              label={isMinDesktop ? t("Ingangsdatum rentevast periode") : t("Ingangsdatum rente")}
              onChange={setLoanpart}
              value={frtStartDate !== null ? frtStartDate : ''}
              errors={errors[`${mortgageIndex}.loanparts.${index}.frtStartDate`]}
              readOnly={readOnly}
            />
            <Select
              name="frtDuration"
              label={t("Periode")}
              onChange={setLoanpart}
              options={frtDurationOptions}
              value={frtDuration}
              styles={{
                menuList: (base) => ({ ...base, maxHeight: '120px' }),
              }}
              errors={errors[`${mortgageIndex}.loanparts.${index}.frtDuration`]}
              isSearchable
              readOnly={readOnly}
            />
            {/* Row */}
            <Date
              name="frtEndDate"
              label={t("Einddatum rentevast periode")}
              onChange={setLoanpart}
              value={frtEndDate !== null ? frtEndDate : ''}
              errors={errors[`${mortgageIndex}.loanparts.${index}.frtEndDate`]}
              readOnly={readOnly}
            />
            <FormFiller />
            {/* Row */}
            <Input
              isPercentage
              name="ltv"
              label={isMinDesktop ? t("Oorspronkelijke tariefklasse") : t("Tariefklasse")}
              onChange={setLoanpart}
              value={ltv}
              errors={errors[`${mortgageIndex}.loanparts.${index}.ltv`]}
              readOnly={readOnly}
            />
            <Input
              isPercentage
              name="interest"
              label={t("Rente")}
              onChange={setLoanpart}
              value={interest}
              errors={errors[`${mortgageIndex}.loanparts.${index}.interest`]}
              readOnly={readOnly}
            />
          </FormStack>
          {!!(interestExpense || repayment || premium || grossMonthlyPayment) && (
            <FormStack space="m" readOnly={true} paddingTop={16}>
              <Input
                isCurrency
                label={t("Rentelast")}
                value={interestExpense}
                readOnly={true}
              />
              <Input
                isCurrency
                label={t("Aflossing")}
                value={repayment}
                readOnly={true}
              />
              <Input
                isCurrency
                label={t("Maandelijkse inleg (premie)")}
                value={premium}
                readOnly={true}
              />
              <Input
                isCurrency
                label={t("Bruto per maand")}
                value={grossMonthlyPayment}
                readOnly={true}
              />
            </FormStack>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

LoanpartSection.propTypes = {
  setLoanpart: func.isRequired,
  removeLoanpart: func.isRequired,
  errors: shape({}),
  index: number.isRequired,
  mortgageIndex: number.isRequired,
  repaymentTypeOptions: array,
  canEditMortgage: bool,

  removable: bool.isRequired,
  loanparttype: string,
  loanpartnumber: string,
  nhg: bool,
  originalAmount: number,
  currentAmount: number,
  startDate: string,
  duration: number,
  frtStartDate: string,
  frtDuration: number,
  ltv: number,
  interest: number,
};

LoanpartSection.defaultProps = {
  errors: {},
  repaymentTypeOptions: [],
  canEditMortgage: false,

  loanparttype: '',
  loanpartnumber: '',
  nhg: false,
  originalAmount: null,
  currentAmount: null,
  startDate: '',
  duration: null,
  frtStartDate: '',
  frtDuration: null,
  ltv: null,
  interest: null,
};

export default LoanpartSection;
